import * as React from 'react';

import { Button, TextArea, cn } from '@rematter/ui-web';
import { Code, AlertCircle } from 'lucide-react';

type JsonTemplateEditorProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  errorMessage?: string;
  description?: string;
  className?: string;
  rows?: number;
};

/**
 * JSON template editor with {{expression}} syntax support
 * Validates JSON structure and highlights template expressions
 */
export const JsonTemplateEditor: React.FC<JsonTemplateEditorProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Enter JSON with {{expression}} syntax...',
  errorMessage,
  description,
  className,
  rows = 8,
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string>('');

  // Detect if the JSON contains template expressions
  const hasExpressions = React.useMemo(
    () => /\{\{.*?\}\}/.test(value),
    [value],
  );

  // Validate JSON structure (with expressions allowed)
  const validateJson = React.useCallback((jsonString: string) => {
    if (!jsonString.trim()) {
      setValidationError('');
      return true;
    }

    try {
      // Replace all {{...}} expressions with placeholder values for validation
      const testJson = jsonString.replace(/\{\{.*?\}\}/g, '"__PLACEHOLDER__"');
      JSON.parse(testJson);
      setValidationError('');
      return true;
    } catch (error) {
      if (error instanceof Error) {
        setValidationError(error.message);
      } else {
        setValidationError('Invalid JSON syntax');
      }
      return false;
    }
  }, []);

  // Validate on value change
  React.useEffect(() => {
    validateJson(value);
  }, [value, validateJson]);

  // Count expressions
  const expressionCount = React.useMemo(() => {
    const matches = value.match(/\{\{.*?\}\}/g);
    return matches ? matches.length : 0;
  }, [value]);

  // Format JSON (preserve expressions)
  const handleFormat = React.useCallback(() => {
    if (!value.trim() || validationError) {
      return;
    }

    try {
      // Extract expressions and replace with placeholders
      const expressions: string[] = [];
      const jsonWithPlaceholders = value.replace(
        /\{\{(.*?)\}\}/g,
        (_match, expr) => {
          expressions.push(expr);
          return `"__EXPR_${expressions.length - 1}__"`;
        },
      );

      // Parse and format
      const parsed = JSON.parse(jsonWithPlaceholders);
      let formatted = JSON.stringify(parsed, null, 2);

      // Restore expressions
      expressions.forEach((expr, index) => {
        formatted = formatted.replace(`"__EXPR_${index}__"`, `{{${expr}}}`);
      });

      onChange(formatted);
    } catch (_error) {
      // Silently fail - validation error is already shown
    }
  }, [value, validationError, onChange]);

  const displayError = errorMessage || validationError;

  return (
    <div className={cn('@JsonTemplateEditor', className)}>
      {label && (
        <label className="mb-1 block text-sm font-medium">
          <div className="flex items-center gap-2">
            <Code className="size-4 text-neutral/60" />
            {label}
          </div>
        </label>
      )}
      {description && (
        <p className="mb-2 text-xs text-neutral/60">{description}</p>
      )}

      <div className="relative">
        <TextArea
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          rows={rows}
          isInvalid={!!displayError}
          className={cn(
            '[&_textarea]:font-mono',
            hasExpressions &&
              !displayError &&
              '[&_.@FieldGroup]:border-primary/40',
          )}
        />
        {isFocused && !displayError && (
          <div className="pointer-events-none absolute top-2 right-2 text-xs text-neutral/40">
            Use {'{{...}}'} for expressions
          </div>
        )}
      </div>

      {/* Format button */}
      {value.trim() && !displayError && (
        <Button variant="ghost" size="small" onPress={handleFormat}>
          Format JSON
        </Button>
      )}

      {/* Error message */}
      {displayError && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-failure/20 bg-failure/10 p-2">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-failure" />
          <p className="text-xs text-failure">{displayError}</p>
        </div>
      )}

      {/* Expression indicator */}
      {hasExpressions && !displayError && (
        <div className="mt-2 flex items-center justify-between rounded-lg border border-primary/20 bg-primary/10 p-2">
          <p className="text-xs text-primary">
            Contains {expressionCount} template{' '}
            {expressionCount === 1 ? 'expression' : 'expressions'}
          </p>
        </div>
      )}

      {/* Help text */}
      {!displayError && (
        <div className="mt-3 rounded-lg border bg-neutral/5 p-3">
          <p className="mb-2 text-xs font-medium">Template Syntax:</p>
          <div className="space-y-1 text-xs text-neutral/70">
            <div>• Use {'{{variable.path}}'} to insert dynamic values</div>
            <div>• Valid JSON structure is required</div>
            <div>• Expressions can be used in string values</div>
            <div>
              • Example: {'{'}
              {'"name"'}: {'"{{user.name}}"'}
              {'}'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
